/**
 * Opener styles (`box_opener_styles`): which animation a box opens with, and the theme tokens that
 * style overrides. A style is resolved box → Shopify shop → environment → platform default, and
 * every level but the platform's is NULL = inherit.
 *
 * `kind` names an animation `@loot-os/react` implements (`OPENER_DESCRIPTORS`, one descriptor per
 * kind); a new animation is a new member here and a descriptor there, never a conditional in the
 * opener shell. `theme` holds overrides only: an opener themes itself from its host (the
 * storefront theme, or the Shopify shop's `opener_theme`), and a token the style leaves unset keeps
 * the host's value.
 */
import { z } from "zod";

import { isHexColor } from "./color.js";
import { OPENER_COLOR_ROLES, type OpenerColorRole } from "./openerTheme.js";
import { STOREFRONT_RARITY_KEYS } from "./rarityTiers.js";
import { compactShopifyOpenerTheme, type ShopifyOpenerThemeDraft } from "./shopifyOpenerTheme.js";

export const OPENER_KINDS = ["spinner", "neon_slam", "neon_pack", "retail_box"] as const;
export type OpenerKind = (typeof OPENER_KINDS)[number];

export const openerKindSchema = z.enum(OPENER_KINDS);

/** The animation every opener falls back to, and the kind of the seeded platform style. */
export const DEFAULT_OPENER_KIND: OpenerKind = "spinner";

/**
 * The platform style the migration seeds ("Classic spinner"): what a box resolves to while no
 * level sets a style, platform default included. It cannot be deleted.
 */
export const DEFAULT_OPENER_STYLE_ID = "01K6B0PENER000000000000000";

/** The platform Neon Slam style migration 0082 seeds; deletable like any other platform style. */
export const NEON_SLAM_OPENER_STYLE_ID = "01K6B0PENER0000000NE0NS1AM";

/** The platform Neon Pack style migration 0082 seeds; deletable like any other platform style. */
export const NEON_PACK_OPENER_STYLE_ID = "01K6B0PENER0000000NE0NPACK";

/** The platform Retail Box style migration 0082 seeds; deletable like any other platform style. */
export const RETAIL_BOX_OPENER_STYLE_ID = "01K6B0PENER0000000RETA1B0X";

/** Each kind's name and one-line description, as the dashboard shows them. */
export const OPENER_KIND_INFO: Record<OpenerKind, { label: string; description: string }> = {
  spinner: {
    label: "Spinner",
    description: "A reel of items that spins past a pointer and settles on the item won.",
  },
  neon_slam: {
    label: "Neon Slam",
    description:
      "A holographic cube slams onto a synthwave grid, shatters into voxels and decrypts the item won. One item per open; a host that has not loaded it opens as the spinner.",
  },
  neon_pack: {
    label: "Neon Pack",
    description:
      "A holo booster pack rips open on a synthwave grid, the empty pack slams the grid and derezzes, and its one card decrypts to the item won. One item per open; a host that has not loaded it opens as the spinner.",
  },
  retail_box: {
    label: "Retail Box",
    description:
      "A product box in the store's colors hops on a studio plinth, the lid pops off and the item won rises out as a silhouette, then the spotlight and camera flashes reveal it. Built for apparel, sneaker and watch stores. One item per open; a host that has not loaded it opens as the spinner.",
  },
};

/**
 * A theme variable one opener kind adds beyond the shared color roles. Unset, it follows
 * `fallback`: a shared role, so it tracks the host's theme, or, where the kind's look needs a
 * color no role plays, a fixed default.
 */
export interface OpenerKindToken {
  key: string;
  label: string;
  fallback: { role: OpenerColorRole } | { color: string };
}

/**
 * Each kind's own theme variables, stored on a style under `theme.kind` and edited beside the
 * shared roles. `@loot-os/react` declares the same list on each descriptor (a test keeps the two
 * in step) and exposes each value as `--los-kind-{key}` and as `theme.kind[key]`.
 */
export const OPENER_KIND_TOKENS: Record<OpenerKind, readonly OpenerKindToken[]> = {
  spinner: [],
  neon_slam: [
    { key: "glow", label: "Glow", fallback: { role: "accent" } },
    { key: "grid", label: "Grid lines", fallback: { color: "#37e6ff" } },
    { key: "env", label: "Reflections", fallback: { color: "#b48cff" } },
  ],
  neon_pack: [
    { key: "glow", label: "Glow", fallback: { role: "accent" } },
    { key: "grid", label: "Grid lines", fallback: { color: "#37e6ff" } },
    { key: "env", label: "Reflections", fallback: { color: "#b48cff" } },
  ],
  retail_box: [
    { key: "box", label: "Box", fallback: { role: "accent" } },
    { key: "stripe", label: "Box print", fallback: { role: "accentForeground" } },
    { key: "tissue", label: "Tissue paper", fallback: { color: "#f3efe6" } },
    { key: "spotlight", label: "Spotlight", fallback: { color: "#fff4e0" } },
  ],
};

/** A kind variable's key: what `theme.kind` is keyed by and what its CSS variable names. */
const kindTokenKeySchema = z.string().regex(/^[a-z][a-zA-Z0-9]{0,31}$/, "a camelCase key");

/** A hex color, the only form the opener theme editors write. */
const openerHexSchema = z.string().refine(isHexColor, "a #rgb or #rrggbb color");

/**
 * Opener theme overrides: the opener's own seven color roles, rarity colors, radius and font, every
 * one optional. The shape of an opener style's `theme` and of a Shopify shop's `opener_theme`.
 * Readers sanitize it again, because the value lands in an inline style on a tenant's page.
 */
export const openerThemeOverridesSchema = z.object({
  colors: z.partialRecord(z.enum(OPENER_COLOR_ROLES), openerHexSchema).optional(),
  rarity: z.partialRecord(z.enum(STOREFRONT_RARITY_KEYS), openerHexSchema).optional(),
  radius: z
    .string()
    .regex(/^\d{1,3}(?:\.\d+)?(px|rem|em|%)?$/, "a length like 12px")
    .optional(),
  fontFamily: z
    .string()
    .regex(/^[\w\s,'"-]{1,100}$/, "a font stack of letters, digits, spaces, commas and quotes")
    .optional(),
  /**
   * The kind variables (`OPENER_KIND_TOKENS`), hex colors keyed by token. A style's writer checks
   * the keys against the style's kind (`kindTokenIssue`); a Shopify shop's theme is not checked,
   * because the kind it sits under follows the shop's style, and an opener reads only the keys its
   * kind declares.
   */
  kind: z.record(kindTokenKeySchema, openerHexSchema).optional(),
});
export type OpenerThemeOverrides = z.infer<typeof openerThemeOverridesSchema>;

export const openerStyleNameSchema = z.string().trim().min(1).max(60);

/** Creating an opener style, in either scope (the tenant's Openers page or platform staff's). */
export const createOpenerStyleInputSchema = z.object({
  name: openerStyleNameSchema,
  kind: openerKindSchema,
  theme: openerThemeOverridesSchema,
});

/** Naming one opener style: what a delete takes. */
export const openerStyleIdInputSchema = z.object({ styleId: z.string().min(1) });

/** Changing an opener style: any of its name, kind or theme (the theme written whole). */
export const updateOpenerStyleInputSchema = openerStyleIdInputSchema.extend({
  name: openerStyleNameSchema.optional(),
  kind: openerKindSchema.optional(),
  theme: openerThemeOverridesSchema.optional(),
});

/** The longest opener brand name, in characters: it has to fit where a logo does. */
export const MAX_OPENER_BRAND_NAME_LENGTH = 40;

/**
 * The tenant's name as an opener prints it where the logo goes (the environment's opener brand
 * name); "" for none. One line of plain text: no control characters.
 */
export const openerBrandNameSchema = z
  .string()
  .trim()
  .max(MAX_OPENER_BRAND_NAME_LENGTH)
  // biome-ignore lint/suspicious/noControlCharactersInRegex: refusing them is the point
  .refine((name) => !/[\u0000-\u001f\u007f]/.test(name), "one line of text");

/**
 * The opener style a box opens with, resolved. `kind` is a plain string on the wire so a client
 * built before a kind existed still parses the box; it falls back to its default animation.
 */
export const boxOpenerSchema = z.object({
  styleId: z.string(),
  kind: z
    .string()
    .describe("The opener animation, one of: spinner, neon_slam, neon_pack, retail_box."),
  theme: openerThemeOverridesSchema.optional(),
  logoSvg: z
    .string()
    .optional()
    .describe(
      "The tenant's logo as SVG source, drawn onto the opener as its brand; absent when it has none. Embed it only as an <img> or Image() data URI (data:image/svg+xml,…), never as inline markup.",
    ),
  brandName: z
    .string()
    .optional()
    .describe(
      "The tenant's name, printed as text where the opener draws the logo when the box carries no logoSvg; absent when it has none.",
    ),
});
export type BoxOpener = z.infer<typeof boxOpenerSchema>;

/** The first `theme.kind` key the style's kind does not declare, or null when every key is one. */
export function kindTokenIssue(kind: OpenerKind, theme: OpenerThemeOverrides): string | null {
  const declared = new Set(OPENER_KIND_TOKENS[kind].map((token) => token.key));
  return Object.keys(theme.kind ?? {}).find((key) => !declared.has(key)) ?? null;
}

/**
 * What a kind variable shows while unset: the color its role has (`roleColor`, the host's), or its
 * fixed default. The editors' blank swatches, as `resolveKindTokens` resolves the opener's.
 */
export function kindTokenFallback(
  token: OpenerKindToken,
  roleColor: (role: OpenerColorRole) => string,
): string {
  return "role" in token.fallback ? roleColor(token.fallback.role) : token.fallback.color;
}

/**
 * An opener theme form as the theme to save, in every editor (the opener style editor and the
 * Shopify shop's two): blank fields dropped, and only the variables `kind` declares kept, so
 * switching the animation leaves the old one's behind. Null when nothing is left.
 */
export function compactOpenerThemeForKind(
  draft: ShopifyOpenerThemeDraft,
  kind: OpenerKind,
): OpenerThemeOverrides | null {
  const theme = compactShopifyOpenerTheme(draft);
  if (theme === null) return null;
  const kept = themeForKind(kind, theme);
  return Object.keys(kept).length ? kept : null;
}

/** The theme with only the kind variables `kind` declares, as a box carries it on the wire. */
export function themeForKind(kind: OpenerKind, theme: OpenerThemeOverrides): OpenerThemeOverrides {
  const { kind: tokens, ...shared } = theme;
  const declared = new Set(OPENER_KIND_TOKENS[kind].map((token) => token.key));
  const kept = Object.fromEntries(
    Object.entries(tokens ?? {}).filter(([key]) => declared.has(key)),
  );
  return Object.keys(kept).length ? { ...shared, kind: kept } : shared;
}
